import { BaseNode } from "./base-node";
import type { TextElement } from "@/timeline";
import type { EffectPass } from "@/effects/types";
import type { BlendMode, Transform } from "@/rendering";
import { drawMeasuredTextLayout, strokeMeasuredTextLayout } from "@/text/primitives";
import type { MeasuredTextElement } from "@/text/measure-element";

export type TextNodeParams = TextElement & {
	transform: Transform;
	opacity: number;
	blendMode?: BlendMode;
	canvasCenter: { x: number; y: number };
	canvasHeight: number;
	textBaseline?: CanvasTextBaseline;
};

export interface ResolvedTextNodeState {
	transform: Transform;
	opacity: number;
	textColor: string;
	backgroundColor: string;
	strokeColor: string;
	effectPasses: EffectPass[][];
	measuredText: MeasuredTextElement;
}

export class TextNode extends BaseNode<TextNodeParams, ResolvedTextNodeState> {}

export function renderTextToContext({
	node,
	ctx,
}: {
	node: TextNode;
	ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
}): void {
	const resolved = node.resolved;
	if (!resolved) {
		return;
	}

	const x = resolved.transform.position.x + node.params.canvasCenter.x;
	const y = resolved.transform.position.y + node.params.canvasCenter.y;
	const baseline = node.params.textBaseline ?? "middle";

	ctx.save();
	ctx.translate(x, y);
	ctx.scale(resolved.transform.scaleX, resolved.transform.scaleY);
	if (resolved.transform.rotate) {
		ctx.rotate((resolved.transform.rotate * Math.PI) / 180);
	}

	// Stroke first, fill on top — the classic canvas text-outline technique (strokeText draws a
	// wider outline centered on the glyph edges; fillText on top then covers the inner half of
	// that outline, leaving only the outer ring visible, i.e. an outline around the text rather
	// than a stroke through it). Drawn before drawMeasuredTextLayout's own background+fill so a
	// background box (if also enabled) paints over the stroke where they'd overlap — stroke and
	// background box are meant as alternative caption looks (see subtitles/style-presets.ts), not
	// layered together.
	const stroke = resolved.measuredText.resolvedStroke;
	if (stroke.enabled && stroke.width > 0) {
		strokeMeasuredTextLayout({
			ctx,
			layout: resolved.measuredText,
			strokeColor: resolved.strokeColor,
			strokeWidth: stroke.width * resolved.measuredText.fontSizeRatio,
			textBaseline: baseline,
		});
	}

	drawMeasuredTextLayout({
		ctx,
		layout: resolved.measuredText,
		textColor: resolved.textColor,
		background: resolved.measuredText.resolvedBackground,
		backgroundColor: resolved.backgroundColor,
		textBaseline: baseline,
	});

	ctx.restore();
}
